/**
 * Adaptive Header & Sidebar Colors
 * Detects background behind transparent header/sidebar and adjusts colors
 * Sticky header colors are handled by CSS
 */

(function() {
  function init() {
    const headerSection = document.getElementById('shopify-section-header');
    if (!headerSection) return;

    const header = headerSection.querySelector('.header');
    if (!header) return;

    const sidebars = document.querySelectorAll('.ssense-sidebar');

    function getLuminance(r, g, b) {
      const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    }

    function parseColor(colorStr) {
      if (!colorStr) return null;
      const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      if (match) {
        const alpha = match[4] !== undefined ? parseFloat(match[4]) : 1;
        if (alpha === 0) return null;
        return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
      }
      return null;
    }

    function sampleImagePixel(img, clientX, clientY) {
      if (!img.complete || !img.naturalWidth) return null;
      const rect = img.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      const style = window.getComputedStyle(img);
      const fit = style.objectFit || 'fill';
      const pos = (style.objectPosition || '50% 50%').split(' ');
      const posX = parseFloat(pos[0]) / 100;
      const posY = parseFloat(pos[1]) / 100;

      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      let dw = rect.width;
      let dh = rect.height;
      let dx = 0;
      let dy = 0;

      if (fit === 'cover') {
        const scale = Math.max(rect.width / iw, rect.height / ih);
        dw = iw * scale;
        dh = ih * scale;
        dx = (rect.width - dw) * (isNaN(posX) ? 0.5 : posX);
        dy = (rect.height - dh) * (isNaN(posY) ? 0.5 : posY);
      } else if (fit === 'contain') {
        const scale = Math.min(rect.width / iw, rect.height / ih);
        dw = iw * scale;
        dh = ih * scale;
        dx = (rect.width - dw) * (isNaN(posX) ? 0.5 : posX);
        dy = (rect.height - dh) * (isNaN(posY) ? 0.5 : posY);
      }

      const localX = clientX - rect.left - dx;
      const localY = clientY - rect.top - dy;
      if (localX < 0 || localY < 0 || localX > dw || localY > dh) return null;

      const sx = (localX / dw) * iw;
      const sy = (localY / dh) * ih;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, 1, 1, 0, 0, 1, 1);
        const data = ctx.getImageData(0, 0, 1, 1).data;
        return { r: data[0], g: data[1], b: data[2] };
      } catch (e) {
        return null;
      }
    }

    // Resolve the background color for a single point. Assumes any
    // sample/header elements that should be "seen through" are already
    // hidden by the caller (batched), so this performs only READS.
    function resolveBackgroundAtPoint(sampleX, sampleY) {
      const element = document.elementFromPoint(sampleX, sampleY);

      if (!element) return { r: 255, g: 255, b: 255 };

      // If we landed on an <img>, sample the actual pixel
      if (element.tagName === 'IMG') {
        const px = sampleImagePixel(element, sampleX, sampleY);
        if (px) return px;
      }

      // Walk up DOM to find background
      let current = element;
      while (current && current !== document.documentElement) {
        const style = window.getComputedStyle(current);

        // Background image/video = assume dark (hero sections)
        if (style.backgroundImage && style.backgroundImage !== 'none') {
          return { r: 0, g: 0, b: 0 };
        }

        // Check for video element
        if (current.tagName === 'VIDEO') {
          return { r: 0, g: 0, b: 0 };
        }

        // If an <img> sits inside this element, prefer sampling its pixel
        if (current.tagName !== 'IMG') {
          const innerImg = current.querySelector ? current.querySelector(':scope > img') : null;
          if (innerImg) {
            const px = sampleImagePixel(innerImg, sampleX, sampleY);
            if (px) return px;
          }
        }

        // Check solid background color
        const bg = parseColor(style.backgroundColor);
        if (bg) return bg;

        current = current.parentElement;
      }

      // Default: white/light background
      return { r: 255, g: 255, b: 255 };
    }

    // Sample one or more points behind a set of elements with a single
    // hide/restore cycle. Hiding writes layout once, all elementFromPoint +
    // getComputedStyle reads happen in one pass, then visibility is restored
    // once — eliminating per-point write->read layout thrash.
    // `points` is an array of { x, y }; returns an array of {r,g,b} in order.
    function getBackgroundAtPoints(points, hideElements) {
      // Single batched WRITE: hide all sample/header elements at once.
      const saved = hideElements.map(el => {
        if (!el) return null;
        const orig = el.style.visibility;
        el.style.visibility = 'hidden';
        return { el, orig };
      }).filter(Boolean);

      const results = [];
      try {
        // Single batched READ pass over every point while hidden.
        for (let i = 0; i < points.length; i++) {
          results.push(resolveBackgroundAtPoint(points[i].x, points[i].y));
        }
      } finally {
        // Single batched WRITE: always restore visibility, even on throw,
        // so the header/sidebars can never get stuck invisible.
        saved.forEach(({ el, orig }) => {
          el.style.visibility = orig || '';
        });
      }

      return results;
    }

    function getBackgroundBehindHeader() {
      const headerRect = header.getBoundingClientRect();
      let sampleY = headerRect.top + headerRect.height / 2;
      if (sampleY < 0) sampleY = 10;

      const floatingLogoEl = document.querySelector('.floating-logo');
      const announcementBar = document.getElementById('shopify-section-announcement-bar');
      const hide = [headerSection, floatingLogoEl, announcementBar];

      /* Mobile: icons sit on the left and right edges, so a single center
         sample reads whatever happens to be in the middle (often a product
         image gutter). Sample left/center/right and vote — go with the
         majority. If majority light, return white (dark icons); if majority
         dark, return black (light icons). */
      if (window.innerWidth < 750) {
        const offsets = [0.15, 0.5, 0.85];
        const points = offsets.map(t => ({
          x: headerRect.left + headerRect.width * t,
          y: sampleY
        }));
        const samples = getBackgroundAtPoints(points, hide);
        let darkVotes = 0;
        let lightVotes = 0;
        samples.forEach(bg => {
          const lum = getLuminance(bg.r, bg.g, bg.b);
          if (lum < 0.4) darkVotes++;
          else lightVotes++;
        });
        if (darkVotes > lightVotes) return { r: 0, g: 0, b: 0 };
        return { r: 255, g: 255, b: 255 };
      }

      const sampleX = headerRect.left + headerRect.width / 2;
      return getBackgroundAtPoints([{ x: sampleX, y: sampleY }], hide)[0];
    }

    function updateHeaderColors() {
      // Skip when sticky - CSS with higher specificity handles sticky state
      if (headerSection.classList.contains('shopify-section-header-sticky')) {
        return;
      }

      // Force light icons when mega menu is open (dark dropdown background)
      const openMega = header.querySelector('.mega-menu[open]');
      if (openMega) {
        headerSection.classList.add('header-light-icons');
        headerSection.classList.remove('header-dark-icons');
        return;
      }

      const bg = getBackgroundBehindHeader();
      const luminance = getLuminance(bg.r, bg.g, bg.b);

      if (luminance < 0.4) {
        // Dark background - use white icons
        headerSection.classList.add('header-light-icons');
        headerSection.classList.remove('header-dark-icons');
      } else {
        // Light background - use dark icons
        headerSection.classList.add('header-dark-icons');
        headerSection.classList.remove('header-light-icons');
      }

      // Sync floating logo inversion with icon colors. On both mobile and
      // desktop, apply CSS filter directly so the video logo flips between
      // white (default) and black (inverted) to stay visible on any bg.
      const floatingLogo = document.querySelector('.floating-logo video');
      if (floatingLogo) {
        floatingLogo.style.filter = (luminance < 0.4) ? 'invert(0)' : 'invert(1)';
        floatingLogo.style.mixBlendMode = 'normal';
        const logoLink = document.getElementById('floating-logo-link');
        if (logoLink) {
          logoLink.dataset.logoInvert = (luminance < 0.4) ? '0' : '1';
        }
      }
    }

    function updateSidebarColors() {
      if (sidebars.length === 0) return;
      if (window.innerWidth < 990) return;

      sidebars.forEach(sb => {
        const sidebarRect = sb.getBoundingClientRect();
        const sampleX = sidebarRect.left + sidebarRect.width / 2;

        // Sample 5 points along the sidebar's vertical axis so a single
        // foreground element (e.g. a card image) bleeding through one spot
        // doesn't flip the whole sidebar.
        const offsets = [0.1, 0.3, 0.5, 0.7, 0.9];
        const points = offsets.map(t => ({
          x: sampleX,
          y: sidebarRect.top + sidebarRect.height * t
        }));
        // One hide/restore cycle for all 5 samples on this sidebar.
        const samples = getBackgroundAtPoints(points, [sb]);
        let darkVotes = 0;
        let lightVotes = 0;

        samples.forEach(bg => {
          const luminance = getLuminance(bg.r, bg.g, bg.b);
          if (luminance < 0.4) darkVotes++;
          else lightVotes++;
        });

        if (darkVotes > lightVotes) {
          sb.classList.add('sidebar-light-text');
          sb.classList.remove('sidebar-dark-text');
        } else {
          sb.classList.add('sidebar-dark-text');
          sb.classList.remove('sidebar-light-text');
        }
      });
    }

    function updateAll() {
      updateHeaderColors();
      updateSidebarColors();
    }

    // Color sampling (elementFromPoint + getComputedStyle) is expensive and
    // forces layout. The header/sidebar tint can lag scroll by ~150ms without
    // being perceptible, so throttle the sampling to at most once per interval.
    const SAMPLE_THROTTLE_MS = 150;
    let lastSampleTime = 0;

    // rAF-throttled scroll handler with an additional time-based throttle on
    // the actual sampling work. The rAF gate keeps us to one tick per frame;
    // the timestamp gate keeps the expensive sampling to ~6.6x/sec max.
    let ticking = false;
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          ticking = false;
          const now = performance.now();
          if (now - lastSampleTime < SAMPLE_THROTTLE_MS) return;
          lastSampleTime = now;
          updateAll();
        });
        ticking = true;
      }
    }

    // Initial check
    lastSampleTime = performance.now();
    updateAll();
    setTimeout(() => {
      lastSampleTime = performance.now();
      updateAll();
    }, 150);

    // Listen for scroll and resize. Resize is not high-frequency and changes
    // layout dimensions, so it bypasses the throttle for an immediate update.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      lastSampleTime = performance.now();
      updateAll();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
